'use client';

import { Badge, Skeleton } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';

import { isAffiliateNewBadgeVisible } from '../_utils/isAffiliateNewBadgeVisible';
import { AFFILIATE_DISCLOSURE } from '../merchants/_constants';
import { resolveRewardRateLabel } from '../merchants/_utils/formatRewardRate';
import { BenefitHomeSectionHeader } from './BenefitHomeSectionHeader';
import { BENEFIT_SECTION_CLASS_NAME } from './BenefitMenuSection';

//* 2열(모바일)·3열(PC) 그리드를 두 줄까지만 채운다.
const MOBILE_TILE_COUNT = 4;
const DESKTOP_TILE_COUNT = 6;

const GRID_CLASS_NAME = 'grid grid-cols-2 gap-12 xl:grid-cols-3';

const useHomeMerchants = () =>
  useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v1/allows/affiliate/merchants/home'](),
    select: (response) => response.context?.contents ?? [],
  });

type HomeMerchant = ReturnType<typeof useHomeMerchants>['data'][number];

const SectionHeading = () => (
  <div className="flex flex-col gap-4">
    <BenefitHomeSectionHeader
      title="쇼핑하고 적립받기"
      isNew={isAffiliateNewBadgeVisible()}
      moreHref={ROUTES.BENEFIT_MERCHANTS.pathname}
    />
    <p className="font-500 text-Gray-400 typo-caption2">
      {AFFILIATE_DISCLOSURE}
    </p>
  </div>
);

const MerchantTile = ({ merchant }: { merchant: HomeMerchant }) => {
  const rewardRateLabel = resolveRewardRateLabel(merchant);
  const isPartnerPoint =
    merchant.rewardCurrencyType === 'AFFILIATE_PARTNER_POINT';
  const isInstantPayout =
    merchant.payoutType === 'BATCH' &&
    merchant.rewardCurrencyType === 'WELLO_POINT';

  return (
    <CustomLink
      href={ROUTES.BENEFIT_MERCHANT_DETAIL.withSearchParams({
        dynamicPath: { merchantCode: merchant.merchantCode },
      })}
      className={cn('group flex flex-col gap-8 rounded-small', FOCUS_RING)}
    >
      <span className="flex h-64 items-center justify-center rounded-small border border-Gray-300 bg-Gray-white p-8 transition-colors duration-150 group-hover:border-Gray-400 group-focus-visible:border-Gray-400 group-active:border-Gray-400 motion-reduce:transition-none">
        <span className="relative size-full">
          <Image
            src={merchant.logoImageUrl}
            alt=""
            fill
            sizes="(min-width: 1280px) 160px, 50vw"
            className="object-contain"
          />
        </span>
      </span>
      <span className="flex flex-col gap-2">
        <span className="truncate font-700 text-Gray-900 typo-body4">
          {merchant.merchantName}
        </span>
        {rewardRateLabel ? (
          <span className="flex items-center gap-4">
            <PointCoin
              currency={merchant.rewardCurrencyType}
              size={isPartnerPoint ? 17 : 14}
            />
            <span
              className={cn(
                'truncate font-700 typo-caption1',
                isPartnerPoint ? 'text-B-600' : 'text-Y-800',
              )}
            >
              {rewardRateLabel}
              {isPartnerPoint ? ' 제휴포인트' : null}
            </span>
          </span>
        ) : null}
        {isInstantPayout ? (
          <Badge dimensions="H20" className="w-fit bg-Y-50 text-Y-700">
            웰포인트 즉시 지급
          </Badge>
        ) : null}
      </span>
    </CustomLink>
  );
};

const ShoppingMerchantGrid = () => {
  const { data: merchants } = useHomeMerchants();

  if (merchants.length === 0) {
    return (
      <section className={BENEFIT_SECTION_CLASS_NAME}>
        <SectionHeading />
        <p className="flex min-h-120 items-center justify-center rounded-medium bg-Gray-50 font-500 text-Gray-500 typo-body4">
          지금 추천할 제휴몰이 없어요.
        </p>
      </section>
    );
  }

  return (
    <section className={BENEFIT_SECTION_CLASS_NAME}>
      <SectionHeading />
      <ul className={GRID_CLASS_NAME}>
        {merchants.slice(0, DESKTOP_TILE_COUNT).map((merchant, index) => (
          <li
            key={merchant.merchantCode}
            className={cn(
              'flex flex-col',
              index >= MOBILE_TILE_COUNT && 'hidden xl:flex',
            )}
          >
            <MerchantTile merchant={merchant} />
          </li>
        ))}
      </ul>
    </section>
  );
};

const ShoppingMerchantGridFallback = () => (
  <section className={BENEFIT_SECTION_CLASS_NAME}>
    <SectionHeading />
    <ul aria-hidden className={GRID_CLASS_NAME}>
      {Array.from({ length: DESKTOP_TILE_COUNT }, (_, index) => (
        <li
          key={index}
          className={cn(
            'flex flex-col gap-8',
            index >= MOBILE_TILE_COUNT && 'hidden xl:flex',
          )}
        >
          <Skeleton className="h-64 rounded-small" />
          <Skeleton typo="body4" className="w-2/3 rounded-xxsmall" />
          <Skeleton typo="caption1" className="w-1/2 rounded-xxsmall" />
        </li>
      ))}
    </ul>
  </section>
);

//* 실패해도 홈의 다른 구획은 그대로 보여야 해서 이 구획만 숨긴다.
export const BenefitShoppingSection = () => (
  <ErrorBoundary fallback={null}>
    <Suspense fallback={<ShoppingMerchantGridFallback />}>
      <ShoppingMerchantGrid />
    </Suspense>
  </ErrorBoundary>
);
